import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import {
    createOrUpdatePartnerSubcontract,
    genericPartnerSubcontractCode,
} from '@/modules/Contract/modules/Partner/services'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import {
    Subcontract,
    subcontractStatus,
} from '@/modules/Contract/modules/Subcontract/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import {
    formatDateWithFormatter,
    isAttachment,
    renderFileItem,
} from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { schemaCreateSubcontractModal } from './schema'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface CreatePartnerSubcontractFormValues {
    subcontract: Omit<
        Subcontract,
        'subcontract_id' | 'employee_id' | 'contract_id' | 'service_id'
    >
    attachments?: File[] | Attachment[]
}

export interface CreatePartnerSubcontractModalProps extends ModalProps {
    contractID: number
    subcontract?: Subcontract
}

const CreatePartnerSubcontractModal = ({
    onClose,
    open,
    contractID,
    subcontract,
}: CreatePartnerSubcontractModalProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'subcontracts',
        mode: 'contains',
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS_PARTNERS_SUBCONTRACTS_CODE, contractID],
        queryFn: () => genericPartnerSubcontractCode({ contractID }),
    })

    const formControl = useForm<CreatePartnerSubcontractFormValues>({
        defaultValues: {
            subcontract: subcontract ?? {
                subcontract_code: data,
                subcontract_name: 'PLHĐ...',
                subcontract_effective_date: dayjs().format('DD-MM-YYYY'),
                subcontract_expiry_date: dayjs().format('DD-MM-YYYY'),
                subcontract_status: 'pending',
            },
            attachments: subcontract?.attachments ?? [],
        },
        resolver: yupResolver(schemaCreateSubcontractModal as any),
    })

    useEffect(() => {
        formControl.setValue('subcontract.subcontract_code', data as string)
    }, [data])

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createOrUpdatePartnerSubcontract },
        {
            successMessage: t(
                `${subcontract ? 'update' : 'add'}-partner-subcontract-success`
            ),
            reactQueryKeys: QUERY_KEYS.SUBCONTRACTS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const { isUploading, uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.SUBCONTRACT_PARTNER,
    })

    const handleCreate = async (values: CreatePartnerSubcontractFormValues) => {
        let attachments: Attachment[] = []
        let attachments_to_delete: number[] = []
        if (values?.attachments && values?.attachments?.length > 0) {
            const attFiles = values?.attachments?.filter(
                (attachment) => attachment instanceof File
            ) as File[]
            const atts = values?.attachments?.filter((attachment) =>
                isAttachment(attachment)
            ) as Attachment[]
            const attIDs = atts?.map(
                (attachment) => attachment.attachment_id
            ) as number[]

            attachments_to_delete = subcontract?.attachments
                ?.filter(
                    (attachment) => !attIDs.includes(attachment.attachment_id)
                )
                ?.map((attachment) => attachment.attachment_id) as number[]
            const attachmentUploadeds = await uploadAsync(attFiles)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
        }

        mutateAsync({
            contractID,
            subcontractID: subcontract?.subcontract_id,
            data: {
                subcontract: {
                    ...values.subcontract,
                    attachments: attachments,
                    subcontract_effective_date: values.subcontract
                        .subcontract_effective_date
                        ? formatDateWithFormatter(
                              values.subcontract.subcontract_effective_date,
                              'YYYY-MM-DD'
                          )
                        : null,
                    subcontract_expiry_date: values.subcontract
                        .subcontract_expiry_date
                        ? formatDateWithFormatter(
                              values.subcontract.subcontract_expiry_date,
                              'YYYY-MM-DD'
                          )
                        : null,
                },
            },
            attachments,
            attachments_to_delete,
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const onDelete = (file: File | Attachment) => {
        const isFile = file instanceof File
        if (isFile) {
            const attachments = formControl.getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return attachment.name !== file.name
                }
                return true
            }) as File[]
            formControl.setValue('attachments', newAttachments)
            return
        }
        if (isAttachment(file)) {
            const attachments = formControl.getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return true
                }
                return attachment.attachment_id !== file.attachment_id
            }) as Attachment[]
            formControl.setValue('attachments', newAttachments)
        }
        return
    }

    return (
        <Dialog
            title={t(
                `${subcontract ? 'update' : 'create'}-partner-subcontract`
            )}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending || isUploading,
                    },
                    {
                        title: t(
                            `${subcontract ? 'update' : 'create'}-subcontract`
                        ),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending || isUploading,
                        disabled: isPending || isUploading,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_code',
                            type: 'textfield',
                            label: t('subcontract_code'),
                            placeholder: t('subcontract_code'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_name',
                            type: 'textfield',
                            label: t('subcontract_name'),
                            placeholder: t('subcontract_name'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_effective_date',
                            type: 'datepicker',
                            label: t('subcontract_effective_date'),
                            placeholder: t('subcontract_effective_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_expiry_date',
                            type: 'datepicker',
                            label: t('subcontract_expiry_date'),
                            placeholder: t('subcontract_expiry_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_status',
                            type: 'select',
                            options: subcontractStatus(t),
                            label: t('subcontract_status'),
                            placeholder: t('subcontract_status'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'subcontract.subcontract_note',
                            type: 'textarea',
                            label: t('subcontract_note'),
                            placeholder: t('subcontract_note'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            label: t('attachments'),
                            multiple: true,
                            renderFileItem: (file) => {
                                return renderFileItem(file, onDelete)
                            },
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreatePartnerSubcontractModal
